import type { LeadInsuranceType } from './analytics';

export type QuoteLeadType = LeadInsuranceType;

export type LeadContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const LEAD_TYPES: QuoteLeadType[] = ['car', 'pet', 'home', 'renters'];

export function isQuoteLeadType(value: string | null | undefined): value is QuoteLeadType {
  return !!value && (LEAD_TYPES as string[]).includes(value);
}

export function parseQuoteLeadType(value: string | null | undefined): QuoteLeadType | null {
  return isQuoteLeadType(value) ? value : null;
}

export function leadTypeLabel(type: QuoteLeadType): string {
  switch (type) {
    case 'pet':
      return 'Pet';
    case 'home':
      return 'Home';
    case 'renters':
      return 'Renters';
    default:
      return 'Car';
  }
}

export function enrichmentPathForLeadType(type: QuoteLeadType): string {
  switch (type) {
    case 'pet':
      return '/pet-insurance';
    case 'home':
      return '/insurance/home';
    case 'renters':
      return '/insurance/new';
    default:
      return '/car-insurance-details';
  }
}

/** Read lead id from generalized or legacy car session keys. */
export function readLeadIdFromSession(): number | undefined {
  const raw = sessionStorage.getItem('leadId') || sessionStorage.getItem('carLeadId');
  if (!raw) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function readLeadContactFromSession(): LeadContact | null {
  const raw = sessionStorage.getItem('leadContact') || sessionStorage.getItem('carLeadContact');
  if (!raw) return null;
  try {
    const contact = JSON.parse(raw) as Partial<LeadContact>;
    return {
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
    };
  } catch {
    return null;
  }
}

export function saveLeadSession(opts: {
  leadId: number;
  contact: LeadContact;
  insuranceType: QuoteLeadType;
}): void {
  sessionStorage.setItem('leadId', String(opts.leadId));
  sessionStorage.setItem('leadContact', JSON.stringify(opts.contact));
  sessionStorage.setItem('insuranceType', opts.insuranceType);
  // Legacy aliases so existing car enrichment / login paths keep working
  sessionStorage.setItem('carLeadId', String(opts.leadId));
  sessionStorage.setItem('carLeadContact', JSON.stringify(opts.contact));
}

export function markLeadConversionFired(): void {
  sessionStorage.setItem('leadConversionFired', '1');
  sessionStorage.setItem('carLeadConversionFired', '1');
}

export function wasLeadConversionFired(): boolean {
  return (
    sessionStorage.getItem('leadConversionFired') === '1' ||
    sessionStorage.getItem('carLeadConversionFired') === '1'
  );
}

export function markLeadEnrichmentStarted(): void {
  sessionStorage.setItem('leadEnrichment', '1');
  sessionStorage.setItem('carLeadEnrichment', '1');
}

export function isLeadEnrichmentStarted(): boolean {
  return (
    sessionStorage.getItem('leadEnrichment') === '1' ||
    sessionStorage.getItem('carLeadEnrichment') === '1'
  );
}

export function clearLeadSession(): void {
  sessionStorage.removeItem('leadId');
  sessionStorage.removeItem('leadContact');
  sessionStorage.removeItem('leadEnrichment');
  sessionStorage.removeItem('leadConversionFired');
  sessionStorage.removeItem('carLeadId');
  sessionStorage.removeItem('carLeadContact');
  sessionStorage.removeItem('carLeadEnrichment');
  sessionStorage.removeItem('carLeadConversionFired');
}
